import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Mail } from 'lucide-react';

const ComingSoon = () => {
  const { language, toggleLanguage, content } = useLanguage();

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* 背景粒子效果 */}
      <div className="absolute inset-0">
        <div className="particles">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        {/* 背景渐变球体 */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </div>

      {/* 主要内容 */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="space-y-12"
        >
          {/* 语言切换按钮 */}
          <motion.button
            onClick={toggleLanguage}
            className="absolute top-8 right-8 p-3 glass rounded-full hover:bg-white/10 transition-colors duration-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Globe className="w-6 h-6 text-white" />
          </motion.button>

          {/* Logo */}
          <motion.div 
            className="flex justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
          >
            <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
              <span className="text-white font-bold text-3xl">B</span>
            </div>
          </motion.div>

          {/* 公司名称 */}
          <motion.h1 
            className="text-6xl md:text-8xl font-bold gradient-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            {content.companyName}
          </motion.h1>

          {/* 标语 */}
          <motion.p 
            className="text-2xl md:text-3xl text-gray-300 font-light"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {content.tagline}
          </motion.p>

          {/* 描述 */}
          <motion.p 
            className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            {content.description}
          </motion.p>

          {/* Coming Soon 标签 */}
          <motion.div
            className="inline-block px-8 py-3 glass rounded-full border border-primary-500/30"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <span className="text-primary-500 font-semibold text-lg">
              {content.language === 'zh' ? '即将上线' : 'Coming Soon'}
            </span>
          </motion.div>


          {/* 联系信息 */}
          <motion.div 
            className="flex justify-center space-x-8 text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
          >
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-primary-500" />
              <span>hi@baolieguoshi.com</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer 
        className="absolute bottom-0 left-0 right-0 py-6 border-t border-white/10 bg-black/20 backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="flex items-center space-x-2 text-gray-400">
              <span className="text-sm">© 2025</span>
              <span className="font-semibold text-primary-500">Boogoo Inc.</span>
              <span className="text-sm">All rights reserved.</span>
            </div>

            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-white hover:text-primary-400 underline underline-offset-4"
            >
              京ICP备2024099754号
            </a>
            
            {/* 装饰线条 */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="w-8 h-px bg-gradient-to-r from-transparent to-primary-500/50"></div>
              <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
              <div className="w-8 h-px bg-gradient-to-l from-transparent to-primary-500/50"></div>
            </div>
            
            {/* 年份装饰 */}
            <div className="text-xs text-gray-500 font-mono">
              2025
            </div>
          </div>
        </div>
      </motion.footer>

    </div>
  );
};

export default ComingSoon;
